import { Stack } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import MenuItem, { MenuItemProps } from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper, { PopperProps } from "@mui/material/Popper";
import { useTheme } from "@mui/styles";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import React, { useEffect, useRef, useState } from "react";

export interface MultilevelDropdownOption<T, S> {
  label: string;
  value: T;
  children?: MultilevelDropdownOption<S, unknown>[];
  hasCustomSubmenu?: boolean;
}

interface Props<T, S> {
  options: MultilevelDropdownOption<T, S>[];
  selectedOption?: T;
  selectedSubOption?: S;
  onChange: (value?: T, subValue?: S) => void;
  className?: string;
  invalid?: boolean;
  disabled?: boolean;

  /** Template for the selected item displayed in dropdown root */
  selectedItemTemplate?: (selectedOption?: T, selectedSubOption?: S) => React.ReactNode;

  /** Template for the the items in the top level dropdown list */
  optionTemplate?: (option: T) => React.ReactNode;

  /** Template for the the items in the submenu dropdown list */
  submenuOptionTemplate?: (option: S, onClick?: (event: React.MouseEvent<HTMLLIElement>) => void) => React.ReactNode;

  /** Template for custom submenu component to use instead of default dropdown list */
  customSubmenuTemplate?: (option: T, onClick?: (selectedOption?: T, selectedSubOption?: S) => void) => React.ReactNode;
}

const useStyles = makeStyles((theme) => ({
  paperRoot: {
    boxSizing: "border-box",
    cursor: "pointer",
    "&:hover": {
      border: `2px solid ${theme.palette.primary.main}`,
    },
    "&:focus": {
      border: `2px solid ${theme.palette.primary.main}`,
    },
  },
  disabled: {
    boxSizing: "border-box",
    color: theme.palette.secondary.main,
    cursor: "default",
    borderRadius: "4px",
    "&:hover": {
      border: "none",
      outline: `${theme.palette.secondary.main} solid 1px`,
    },
  },
  root: {
    display: "flex",
    alignItems: "center",
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingTop: "6px",
    paddingBottom: "6px",
    justifyContent: "space-between",
    flexDirection: "row",
  },
  invalid: {
    borderColor: theme.palette.error.main,
  },
  dropdownIcon: {
    color: "#6C757D",
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    height: "100%",
  },
  submenuItemContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  submenuIcon: {
    color: "#6C757D",
    height: "100%",
  },
  popper: {
    zIndex: 1200,
  },
}));

export function MultilevelDropdown<T extends unknown, S extends unknown>(props: Props<T, S>) {
  const classes = useStyles();
  const dropdownRootRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | undefined>();
  const theme = useTheme();

  const handleRootClick = (event: React.MouseEvent<HTMLDivElement>) => {
    !props.disabled && setAnchorEl(anchorEl ? undefined : event.currentTarget);
  };

  const handleClickEvent = (event: React.MouseEvent<HTMLLIElement>) => {
    const { value, subvalue } = event.currentTarget.dataset;
    onSelect(value as T, subvalue as S);
  };

  const handleClickAway = () => {
    setAnchorEl(undefined);
  };

  const onSelect = (value?: T, subvalue?: S) => {
    setAnchorEl(undefined);
    props.onChange(value as T, subvalue as S);
  };

  const popperWidth =
    dropdownRootRef?.current?.offsetWidth && dropdownRootRef?.current?.offsetWidth < 300
      ? "300px"
      : dropdownRootRef?.current?.offsetWidth;

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Paper
          variant="outlined"
          className={clsx(classes.paperRoot, props.className, {
            [classes.disabled]: props.disabled,
            [classes.invalid]: props.invalid,
          })}
        >
          <div
            ref={dropdownRootRef}
            className={clsx(classes.root, props.className, {
              [classes.disabled]: props.disabled,
              [classes.invalid]: props.invalid,
            })}
            onClick={handleRootClick}
            style={{
              ...(props.disabled && {
                color: theme.palette.darkBackground.contrastText,
                cursor: "defualt",
              }),
            }}
          >
            <Stack direction="row" alignItems={"center"}>
              {props.selectedItemTemplate ? (
                props.selectedItemTemplate(props.selectedOption, props.selectedSubOption)
              ) : (
                <>{props.options.find((option) => option.value === props.selectedOption)?.label}</>
              )}
            </Stack>
            <span>
              <i
                className={clsx("pi", "pi-chevron-down", classes.dropdownIcon)}
                style={{ color: theme.palette.darkBackground.contrastText }}
              />
            </span>
          </div>

          <Popper
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            style={{ width: popperWidth }}
            className={classes.popper}
          >
            <Paper
              elevation={5}
              sx={{
                maxHeight: "40vh",
                overflowY: "auto",
                // Switches the scrollbar to the left so the sub components menu
                // can be clicked without losing focus on the scrollbar
                // For RTL languages this is not needed.
                direction: "rtl",
                // Switches the scrollbar back to the right.
                // If this is done on a normally rtl page it will break things further down the page.
                "& > *": {
                  direction: "ltr",
                },
              }}
            >
              <MenuList>
                {props.options.map((option) => {
                  if (option.children || option.hasCustomSubmenu)
                    return (
                      <MenuItemWithSubmenu
                        key={`MenuItemWithSubmenu-${option.value}`}
                        data-value={`${option.value}`}
                        autoFocus={false}
                        label={props.optionTemplate ? props.optionTemplate(option.value) : option.label}
                        selected={option.value === props.selectedOption}
                        open={option.value === props.selectedOption}
                        className={classes.submenuItemContainer}
                      >
                        {props.customSubmenuTemplate
                          ? props.customSubmenuTemplate(option.value, onSelect)
                          : option.children?.map((submenuOption) => {
                              return (
                                <MenuItem
                                  key={`submenuoption-${option.value}-${submenuOption.value}`}
                                  data-value={`${option.value}`}
                                  data-subvalue={`${submenuOption.value}`}
                                  onClick={handleClickEvent}
                                  selected={submenuOption.value === props.selectedSubOption}
                                >
                                  {props.submenuOptionTemplate
                                    ? props.submenuOptionTemplate(submenuOption.value, handleClickEvent)
                                    : submenuOption.label}
                                </MenuItem>
                              );
                            })}
                      </MenuItemWithSubmenu>
                    );
                  else
                    return (
                      <MenuItem
                        key={`menuoption-${option.value}`}
                        data-value={`${option.value}`}
                        onClick={handleClickEvent}
                        selected={option.value === props.selectedOption}
                      >
                        {props.optionTemplate ? props.optionTemplate(option.value) : option.label}
                      </MenuItem>
                    );
                })}
              </MenuList>
            </Paper>
          </Popper>
        </Paper>
      </ClickAwayListener>
    </>
  );
}

interface MenuItemWithSubmenuProps extends MenuItemProps, Pick<PopperProps, "placement"> {
  button?: true;
  label: string | React.ReactNode;
  open?: boolean;
}

const MenuItemWithSubmenu = (props: MenuItemWithSubmenuProps) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    props.open && Boolean(ref.current) && setOpen(true);
  }, [props.open, ref]);

  return (
    <MenuItem {...props} ref={ref} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <span>{props.label} </span>
      <i className={clsx("pi", "pi-chevron-right", classes.submenuIcon)} />
      <Popper
        anchorEl={ref.current}
        open={open}
        placement={props.placement ?? "right-start"}
        className={classes.popper}
        modifiers={[
          {
            name: "flip",
            enabled: true,
          },
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              rootBoundary: "viewport",
            },
          },
        ]}
      >
        <Paper elevation={5}>{props.children}</Paper>
      </Popper>
    </MenuItem>
  );
};
